import {
    Component,
    Host,
    h,
    Element,
    Prop,
    State,
    ComponentInterface,
} from "@stencil/core";
import { ProductionStatus } from "../production-status/production-status-enum";
import { Status } from "../status-label/status";
import { defineCustomElement as defineAuthImage } from "@trumpf/ux.application-framework/dist/components/uxc-auth-image";

defineAuthImage();

// todo: refactor component, adjust naming -> ux-iws-machine-tile
@Component({
    tag: "ux-iws-tile",
    styleUrl: "tile.scss",
})
export class Tile implements ComponentInterface {
    private baseClass = "ux-iws-tile";

    @Element() domNode: HTMLUxIwsTileElement;

    /** name of the machine  */
    @Prop({ reflect: true }) machineName: string;

    /** id of the machine */
    @Prop({ reflect: true }) machineId: string;

    /** url for machine image */
    @Prop({ reflect: true }) machineImageUrl: string;

    /** indicates that ux-auth-image should be used */
    @Prop({ reflect: true }) authImage: boolean;

    /** shop link url */
    @Prop({ reflect: true }) shopLink: string;

    /** shop type */
    @Prop({ reflect: true }) shopLinkType: string;

    /** shop link title which is shown on hover or as button title */
    @Prop({ reflect: true }) shopLinkTitle: string;

    /** @deprecated support for NOT Stateful version -> shop icon which is shown as part of the button when overlay is active */
    @Prop({ reflect: true }) shopLinkIcon: string;

    /** shop link target */
    @Prop({ reflect: true }) shopLinkTarget: string = "";

    /** second shop link */
    @Prop({ reflect: true }) shopLink2: string;

    /** second shop type */
    @Prop({ reflect: true }) shopLink2Type: string;

    /** shop link title for second shop which is shown on hover or as button title */
    @Prop({ reflect: true }) shopLink2Title: string;

    /** @deprecated support for NOT Stateful version -> shop link title for second shop which is shown on hover or as button title */
    @Prop({ reflect: true }) shopLink2Icon: string;

    /** shop link target */
    @Prop({ reflect: true }) shopLink2Target: string = "";

    /** link to detail page of machine */
    @Prop({ reflect: true }) detailLink: string;

    /** text which is displayed on the overlay */
    @Prop({ reflect: true }) chooseShopTitle: string;

    /** Link to create a new service call via a modal */
    @Prop() serviceCallModalLink: string = "";

    /** Defines if the option for the creation of a service case is active */
    @Prop({ reflect: true }) serviceCaseCreationDisabled: boolean;

    /** @deprecated support for NOT Stateful version -> Header text for the case creation disabled explaination tooltip */
    @Prop() serviceCaseCreateDisabledHeaderText: string | null;

    /** @deprecated support for NOT Stateful version -> Body text for the case creation disabled explaination tooltip */
    @Prop() serviceCaseCreateDisabledBodyText: string | null;

    /** id for syncing favorite status */
    @Prop({ reflect: true }) syncId: string;

    /** bool for wether a machine was favorized */
    @Prop({ reflect: true }) isFav: boolean;

    /** url to toggle the favorite status of a machine  */
    @Prop({ reflect: true }) toggleFavUrl: string;

    /** indicates that the production year is lower than the IB1 date */
    @Prop({ reflect: true }) inProduction: string | false = false;

    /** indicates if software updates are available */
    @Prop({ reflect: true }) softwareUpdates: string | false = false;

    /** @deprecated support for NOT Stateful version -> software updates state */
    @Prop({ reflect: true }) softwareUpdatesState: Status | false = false;

    /** @deprecated support for NOT Stateful version -> software updates label */
    @Prop({ reflect: true }) softwareUpdatesLabel: string | false = false;

    /** translate function to get the translation for a key */
    @Prop() translateFn?: (key: string) => string;

    @State() shopLinksLength: number;
    @State() overlayVisibility: boolean = false;
    @State() shopLinksArray: string[] = [];

    componentWillLoad(): void {
        [this.shopLink, this.shopLink2].forEach((link) => {
            if (link === undefined) return;
            this.shopLinksArray.push(link);
        });
        this.shopLinksLength = this.shopLinksArray.length;
    }

    /** tile click opens the detail link if present */
    private tileClick = (e: Event) => {
        let target = e.target as HTMLElement;
        while (target !== this.domNode) {
            if (
                target.tagName === "BUTTON" ||
                target.tagName === "A" ||
                target.classList.contains("ux-tile-body__overlay")
            ) {
                return;
            }
            target = target.parentElement as HTMLElement;
        }

        if (!this.detailLink) {
            return;
        }
        window.location.href = this.detailLink;
    };

    private getShopIcon = (shopLinkType) => {
        switch (shopLinkType) {
            case "spareparts":
                return "cart-cogs";
            case "bending":
                return "cart-bending-tools";
            case "punching":
                return "cart-punching-tools";
            default:
                return "";
        }
    };

    render() {
        const modalName = `modal${this.machineId}`;
        const tileBodyStyles = this.authImage
            ? {
                  position: `relative`,
              }
            : this.inProduction === false
              ? {
                    backgroundImage: `url(${this.machineImageUrl})`,
                    backgroundSize: `cover`,
                }
              : {
                    // give opacity to the background without affecting the children elements
                    background: `linear-gradient(rgba(255,255,255,.6), rgba(255,255,255,.6)), url(${this.machineImageUrl})`,
                    backgroundSize: `cover`,
                };

        const hasDetailLink = this.detailLink ? true : false;
        const shopIcon1 = this.getShopIcon(this.shopLinkType);
        const shopIcon2 = this.getShopIcon(this.shopLink2Type);

        return (
            <Host
                class={{
                    [this.baseClass]: true,
                    ["ux-col-12 ux-col-md-6 ux-col-xl-4"]: true,
                    ["is-link"]: hasDetailLink,
                }}
            >
                <div class="ux-tile" onClick={this.tileClick}>
                    <div class="ux-tile-head">
                        <div class="ux-tile-head__icon">
                            <ux-toggle-button
                                sync-id={`ux-iws-${this.syncId}`}
                                icon-on="fav-filled"
                                icon-off="fav"
                                toggle-url={this.toggleFavUrl}
                                key={this.toggleFavUrl}
                                on={this.isFav}
                                onClick={(e: Event) => e.stopPropagation()}
                            ></ux-toggle-button>
                            <slot name="discontinuation-icon"></slot>
                        </div>
                        <div class="ux-tile-head__text">
                            <div class="ux-tile-head__title">
                                {this.machineName}
                            </div>
                            <div class="ux-tile-head__description">
                                <div class="ux-tile-head__subtitle">
                                    {this.machineId}
                                </div>
                            </div>
                            <div class="ux-tile-head__status">
                                {this.inProduction && (
                                    <ux-iws-production-status
                                        type={ProductionStatus.IN_PRODUCTION}
                                    >
                                        {this.inProduction}
                                    </ux-iws-production-status>
                                )}
                                {this.softwareUpdates && (
                                    <ux-w-status type={this.softwareUpdates}>
                                        {this.translateFn &&
                                            this.translateFn(
                                                `softwareUpdates.values.${this.softwareUpdates}`
                                            )}
                                    </ux-w-status>
                                )}
                                {this.softwareUpdatesState &&
                                    this.softwareUpdatesLabel && (
                                        <ux-iws-status-label
                                            type={this.softwareUpdatesState}
                                        >
                                            {this.softwareUpdatesLabel}
                                        </ux-iws-status-label>
                                    )}
                            </div>
                        </div>
                    </div>
                    <div class="ux-tile-body" style={tileBodyStyles}>
                        {this.authImage && (
                            <uxc-auth-image src={this.machineImageUrl}>
                                {/* @ts-expect-error src is provided by auth-image */}
                                <ux-image alt={this.machineName}></ux-image>
                            </uxc-auth-image>
                        )}
                        <div class="ux-tile-body__button-group">
                            {this.serviceCallModalLink !== "" && (
                                <ux-iws-async-modal
                                    src={this.serviceCallModalLink}
                                    name={modalName}
                                ></ux-iws-async-modal>
                            )}
                            {this.serviceCaseCreationDisabled && (
                                <ux-tooltip
                                    tooltip-target="button[name='service-case-creation-disabled-btn']"
                                    align="top"
                                >
                                    <div class="flex flex--col flex--align-center">
                                        <span
                                            style={{
                                                color: `red`,
                                            }}
                                            class="ux-u-mb-xxxs"
                                        >
                                            <ux-icon
                                                name="close"
                                                size-l="32"
                                                weight="normal"
                                            ></ux-icon>
                                        </span>
                                        <h6 class="ux-u-ml-xxxs ux-u-mt-xxxs ux-u-mb-zero ux-text-headline ux-text-headline--small ux-u-text-align--center">
                                            {(this.translateFn &&
                                                this.translateFn(
                                                    "disabledCreateCase.headerText"
                                                )) ||
                                                this
                                                    .serviceCaseCreateDisabledHeaderText}
                                        </h6>
                                    </div>
                                    <p class="ux-text-normal">
                                        {(this.translateFn &&
                                            this.translateFn(
                                                "disabledCreateCase.bodyText"
                                            )) ||
                                            this
                                                .serviceCaseCreateDisabledBodyText}
                                    </p>
                                </ux-tooltip>
                            )}
                            {(this.serviceCallModalLink !== "" ||
                                this.serviceCaseCreationDisabled) && (
                                <button
                                    name={
                                        this.serviceCaseCreationDisabled
                                            ? "service-case-creation-disabled-btn"
                                            : "service-case-creation-btn"
                                    }
                                    class="ux-button ux-button--icon-only"
                                    data-testid="create-service-case"
                                    disabled={this.serviceCaseCreationDisabled}
                                    data-trigger-async-modal={
                                        this.serviceCallModalLink !== ""
                                            ? `open,${modalName}`
                                            : undefined
                                    }
                                >
                                    <ux-icon name="hand-plus-right" />
                                </button>
                            )}
                            {this.shopLinksLength === 1 && (
                                <a
                                    href={this.shopLink}
                                    class="ux-button ux-button--icon-only"
                                    aria-label={
                                        this.translateFn &&
                                        this.translateFn(
                                            `shopLinks.${this.shopLinkType}`
                                        )
                                    }
                                    data-testid="shop-link"
                                    target="_blank"
                                >
                                    <ux-icon name="cart" />
                                </a>
                            )}
                            {this.shopLinksLength === 2 && (
                                <button
                                    class="ux-button ux-button--icon-only"
                                    onClick={() =>
                                        (this.overlayVisibility =
                                            !this.overlayVisibility)
                                    }
                                    data-testid="shop-link-multiple"
                                >
                                    <ux-icon name="cart" />
                                </button>
                            )}
                            {hasDetailLink && (
                                <a
                                    href={this.detailLink}
                                    class="ux-button ux-button--icon-only"
                                    data-testid="details"
                                >
                                    {" "}
                                    <ux-icon
                                        name="chevron-right"
                                        weight="normal"
                                    ></ux-icon>
                                </a>
                            )}
                        </div>

                        <div
                            class={`ux-tile-body__overlay ${
                                this.overlayVisibility ? "is-visible" : ""
                            }`}
                        >
                            <div
                                class="ux-tile-body__overlay-close"
                                onClick={() =>
                                    (this.overlayVisibility =
                                        !this.overlayVisibility)
                                }
                                data-testid="close-overlay"
                            >
                                <ux-icon name="close"></ux-icon>
                            </div>
                            <div class="ux-tile-body__overlay-content">
                                <div>{this.chooseShopTitle}</div>
                                <a
                                    href={this.shopLink}
                                    class="ux-button"
                                    aria-label={
                                        this.translateFn &&
                                        this.translateFn(
                                            `shopLinks.${this.shopLinkType}`
                                        )
                                    }
                                    target="blank"
                                >
                                    {this.shopLinkTitle}
                                    <ux-icon
                                        name={shopIcon1 || this.shopLinkIcon}
                                    />
                                </a>
                                <a
                                    href={this.shopLink2}
                                    class="ux-button"
                                    aria-label={
                                        this.translateFn &&
                                        this.translateFn(
                                            `shopLinks.${this.shopLink2Type}`
                                        )
                                    }
                                    target="blank"
                                >
                                    {this.shopLink2Title}
                                    <ux-icon
                                        name={shopIcon2 || this.shopLink2Icon}
                                    />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </Host>
        );
    }
}
